import { ExternalEventSource, TourAllocationDeviceType, TourVisibilitySourceType } from '../lib/graphql';

export type CheckStatus = 'ok' | 'warning' | 'failed' | 'pending' | 'unknown';

export type CheckId = 'allocation' | 'sms' | 'login' | 'appTracking' | 'positions' | 'tourState';

export interface Check {
  id: CheckId;
  label: string;
  status: CheckStatus;
  detail: string;
  source: string;
}

export type Health = 'healthy' | 'attention' | 'failing' | 'upcoming';

export interface Issue {
  title: string;
  detail: string;
  nextStep: string;
}

export interface Diagnosis {
  health: Health;
  checks: Check[];
  issue: Issue | null;
  unknownChecks: Check[];
  positionsReceived: boolean;
}

export interface DriverLoginEvent {
  eventSource: ExternalEventSource;
  externalEventQualifier: string;
  externalEventSource: string | null;
  timestamp: string;
}

export interface MobileInvitationSignal {
  status: 'COMPOSED' | 'NOT_COMPOSED' | 'UNKNOWN';
  sentAt: string;
}

export interface MobileAppEventSignal {
  name: string;
  timestamp: string;
  params: { key: string; value: string }[];
}

export interface MobileTourSignals {
  invitations: MobileInvitationSignal[] | null;
  appEvents: MobileAppEventSignal[] | null;
}

export interface DiagnosableTour {
  tour_id: number;
  status: string | null;
  start_time: string | null;
  warnings: { type: string }[];
  failureReason: { type: string } | null;
  vehicleAllocations: { allocationDeviceType: TourAllocationDeviceType | null }[];
  visibilitySources: { primaryEventSource: TourVisibilitySourceType }[];
  externalEvents?: DriverLoginEvent[];
  signals?: MobileTourSignals;
}

export const DRIVER_LOGGED_IN_QUALIFIER = 'DRIVER_LOGGED_IN';
export const DRIVER_LOGIN_EVENTS_SINCE = '2026-10-07T00:00:00Z';

const MOBILE_SOURCES: TourVisibilitySourceType[] = [
  TourVisibilitySourceType.MOBILE,
  TourVisibilitySourceType.TTC_MOBILE,
];
const UPCOMING_STATUSES = ['unallocated', 'allocated', 'waiting', 'waiting-tracking'];
const FAILED_STATUSES = ['failed', 'aborted', 'abandoned'];

const LOGIN_APP_LABEL: Record<string, string> = {
  drive_app: 'Visibility Drive',
  legacy_drive_app: 'Legacy app',
};

export const hasMobileAllocation = (tour: Pick<DiagnosableTour, 'vehicleAllocations'>) =>
  tour.vehicleAllocations.some(({ allocationDeviceType }) => allocationDeviceType === TourAllocationDeviceType.MOBILE);

export const getPrimaryAllocation = <T extends { allocationDeviceType: TourAllocationDeviceType | null }>(
  allocations: T[],
): T | undefined =>
  allocations.find(({ allocationDeviceType }) => allocationDeviceType === TourAllocationDeviceType.MOBILE) ??
  allocations[0];

export const hasMobilePositions = (tour: Pick<DiagnosableTour, 'visibilitySources'>) =>
  tour.visibilitySources.some(({ primaryEventSource }) => MOBILE_SOURCES.includes(primaryEventSource));

export const isMobileTour = (tour: DiagnosableTour) => hasMobileAllocation(tour) || hasMobilePositions(tour);

export const getDriverLoginEvents = (tour: Pick<DiagnosableTour, 'externalEvents'>): DriverLoginEvent[] =>
  (tour.externalEvents ?? [])
    .filter(
      ({ eventSource, externalEventQualifier }) =>
        eventSource === ExternalEventSource.DRIVE_APP && externalEventQualifier === DRIVER_LOGGED_IN_QUALIFIER,
    )
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp));

export const getLoginAppLabel = (event: DriverLoginEvent) =>
  LOGIN_APP_LABEL[event.externalEventSource ?? ''] ?? 'Driver app';

const LOCATION_PERMISSION_EVENTS = ['LOCATION_PERMISSION_GRANTED', 'LOCATION_PERMISSION_DENIED'];

const smsCheck = (tour: DiagnosableTour, loggedIn: boolean): Pick<Check, 'status' | 'detail'> => {
  if (tour.signals === undefined) {
    return { status: 'unknown', detail: 'Open the tour to load invitation status' };
  }
  const { invitations } = tour.signals;
  if (invitations === null) {
    return { status: 'unknown', detail: 'Invitation status could not be loaded' };
  }
  const latest = [...invitations].sort((a, b) => b.sentAt.localeCompare(a.sentAt))[0];
  if (latest === undefined) {
    return { status: 'unknown', detail: 'No invitation recorded' };
  }
  switch (latest.status) {
    case 'COMPOSED':
      return { status: 'ok', detail: 'Handed to the SMS provider, delivery is not confirmed' };
    case 'UNKNOWN':
      return { status: 'pending', detail: 'Invitation is still being processed' };
    case 'NOT_COMPOSED':
      return { status: loggedIn ? 'warning' : 'failed', detail: 'Invitation was not turned into an SMS' };
    default: {
      const unhandled: never = latest.status;
      throw new Error(`Unhandled invitation status ${String(unhandled)}`);
    }
  }
};

const appTrackingCheck = (tour: DiagnosableTour): Pick<Check, 'status' | 'detail'> => {
  if (tour.signals === undefined) {
    return { status: 'unknown', detail: 'Open the tour to load app events' };
  }
  const { appEvents } = tour.signals;
  if (appEvents === null) {
    return { status: 'unknown', detail: 'App events could not be loaded' };
  }
  const latestPermission = appEvents
    .filter(({ name }) => LOCATION_PERMISSION_EVENTS.includes(name))
    .sort((a, b) => b.timestamp.localeCompare(a.timestamp))[0];
  if (latestPermission === undefined) {
    return {
      status: 'unknown',
      detail: appEvents.length === 0 ? 'No app events recorded' : 'No location permission events recorded',
    };
  }
  const permissionType = latestPermission.params.find(({ key }) => key === 'permission_type')?.value ?? 'location';
  return latestPermission.name === 'LOCATION_PERMISSION_GRANTED'
    ? { status: 'ok', detail: `Driver granted ${permissionType} location permission` }
    : { status: 'warning', detail: `Driver denied ${permissionType} location permission` };
};

const ISSUES: Record<CheckId, Issue> = {
  allocation: {
    title: 'No mobile allocation',
    detail: 'The transport has no phone allocated, so the driver cannot log in with the transport number.',
    nextStep: 'Check the carrier mobile allocation flags and whether the carrier allocated a phone number.',
  },
  sms: {
    title: 'Driver invitation SMS was not sent',
    detail: 'The invitation never became an SMS, so the driver probably never received the link and transport number.',
    nextStep: 'Verify the allocated phone number format and the notification-service logs for this tour.',
  },
  login: {
    title: 'No driver login recorded',
    detail: 'The driver has not logged into the transport in the app.',
    nextStep:
      'Compare the transport number the driver used with this tour, and check the legacy credentials flag on the carrier.',
  },
  appTracking: {
    title: 'Driver denied location permission',
    detail: 'The app cannot share location without the permission.',
    nextStep: 'Ask the driver to allow background location and keep the app open, then check app version and OS.',
  },
  positions: {
    title: 'No mobile positions reach Visibility',
    detail: 'Sixfold has no mobile positions for this tour.',
    nextStep: 'Check API traffic for position errors and the Kafka position stream for this transport.',
  },
  tourState: {
    title: 'Tour has warnings or failed',
    detail: 'Mobile tracking works, but the tour itself reports problems.',
    nextStep: 'Open the full tour view to inspect the warnings and the failure reason.',
  },
};

const STATUS_RANK: Record<CheckStatus, number> = { failed: 3, warning: 2, pending: 1, ok: 0, unknown: 0 };

export const diagnoseTour = (tour: DiagnosableTour): Diagnosis => {
  const status = (tour.status ?? '').toLowerCase();
  const upcoming = UPCOMING_STATUSES.includes(status);
  const positionsReceived = hasMobilePositions(tour);
  const allocated = hasMobileAllocation(tour);
  const notYet: CheckStatus = upcoming ? 'pending' : 'failed';

  const loginCheck = (): Pick<Check, 'status' | 'detail'> => {
    if (tour.externalEvents === undefined) {
      return { status: 'unknown', detail: 'Open the tour to load login events' };
    }
    const events = getDriverLoginEvents(tour);
    if (events.length > 0) {
      return {
        status: 'ok',
        detail: `${getLoginAppLabel(events[0])}, ${events.length} login event${events.length > 1 ? 's' : ''} recorded`,
      };
    }
    const reference = tour.start_time;
    if (reference === null || reference < DRIVER_LOGIN_EVENTS_SINCE) {
      return { status: 'unknown', detail: 'Login events are recorded since 7 Oct 2026' };
    }
    return upcoming
      ? { status: 'pending', detail: 'Driver has not logged in yet' }
      : { status: 'warning', detail: 'No login recorded' };
  };

  const checks: Check[] = [
    {
      id: 'allocation',
      label: 'Mobile allocation',
      source: 'Tour allocation',
      ...(allocated
        ? { status: 'ok', detail: 'Phone allocated to the transport' }
        : positionsReceived
          ? { status: 'ok', detail: 'Tracking without explicit allocation' }
          : { status: notYet, detail: upcoming ? 'Not allocated yet' : 'No phone allocated' }),
    },
    {
      id: 'sms',
      label: 'Invitation SMS',
      source: 'Notification messages',
      ...smsCheck(tour, loginCheck().status === 'ok'),
    },
    { id: 'login', label: 'Driver login', source: 'Tour external events', ...loginCheck() },
    {
      id: 'appTracking',
      label: 'App tracking',
      source: 'Firebase Analytics',
      ...appTrackingCheck(tour),
    },
    {
      id: 'positions',
      label: 'Positions in Visibility',
      source: 'Tour visibility sources',
      ...(positionsReceived
        ? { status: 'ok', detail: 'Mobile positions are received' }
        : { status: notYet, detail: 'No mobile positions received' }),
    },
    {
      id: 'tourState',
      label: 'Tour state',
      source: 'Tour status',
      ...(tour.failureReason !== null || FAILED_STATUSES.includes(status)
        ? { status: 'failed', detail: tour.failureReason?.type ?? status }
        : tour.warnings.length > 0
          ? { status: 'warning', detail: tour.warnings.map(({ type }) => type).join(', ') }
          : { status: 'ok', detail: 'No warnings' }),
    },
  ];

  const worst = checks.reduce((acc, check) => (STATUS_RANK[check.status] > STATUS_RANK[acc.status] ? check : acc));
  const firstProblem = checks.find((check) => check.status === 'failed' || check.status === 'warning');

  const health: Health =
    upcoming && worst.status !== 'failed'
      ? 'upcoming'
      : worst.status === 'failed'
        ? 'failing'
        : worst.status === 'warning'
          ? 'attention'
          : 'healthy';

  return {
    health,
    checks,
    issue: firstProblem === undefined ? null : ISSUES[firstProblem.id],
    unknownChecks: checks.filter(({ status: checkStatus }) => checkStatus === 'unknown'),
    positionsReceived,
  };
};

export const HEALTH_LABEL: Record<Health, string> = {
  healthy: 'Healthy',
  attention: 'Needs attention',
  failing: 'Failing',
  upcoming: 'Upcoming',
};

export const HEALTH_KIND: Record<Health, 'success' | 'warning' | 'danger' | 'neutral'> = {
  healthy: 'success',
  attention: 'warning',
  failing: 'danger',
  upcoming: 'neutral',
};

export const HEALTH_ORDER: Record<Health, number> = { failing: 0, attention: 1, upcoming: 2, healthy: 3 };
